import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getPersonnelHoursHistory } from '../services/api';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatAuditChangeLines, formatAuditTimestamp } from '../utils/personnelHoursAuditDisplay';

const historyCache = new Map();

function PersonnelHoursHistoryList({ entryId, compact = false }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!entryId) {
      setRows([]);
      return undefined;
    }

    if (historyCache.has(entryId)) {
      setRows(historyCache.get(entryId));
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getPersonnelHoursHistory(entryId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || [];
        historyCache.set(entryId, data);
        setRows(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message);
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [entryId]);

  if (!entryId) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: compact ? 0.5 : 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          {t('personnelHoursHistory.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: compact ? 0.5 : 1 }}>
        {error}
      </Typography>
    );
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: compact ? 0.5 : 1 }}>
        {t('personnelHoursHistory.noHistory')}
      </Typography>
    );
  }

  return (
    <List dense disablePadding sx={{ py: compact ? 0 : 0.5 }}>
      {rows.map((log) => {
        const userLabel = log.changedByUser?.email || t('personnelHoursHistory.unknownUser');
        const when = formatAuditTimestamp(log.changedAt, formatDateEuropean);
        const changeLines = formatAuditChangeLines(log, t);

        return (
          <ListItem key={log.id} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
            <ListItemText
              primary={(
                <Typography variant="body2" component="span">
                  {t('personnelHoursHistory.byUser', { user: userLabel, date: when })}
                </Typography>
              )}
              secondary={(
                <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                  {changeLines.map((line) => (
                    <Typography key={line} variant="caption" component="div" color="text.secondary">
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            />
          </ListItem>
        );
      })}
    </List>
  );
}

export function clearPersonnelHoursHistoryCache() {
  historyCache.clear();
}

export default PersonnelHoursHistoryList;
